/** Rôles métier du JWT : plusieurs rôles peuvent désigner la même installation. */
export const ROLES_INSTALLATION = ['mo', 'visu', 'maint', 'install', 'be', 'admin'] as const;
export type RoleInstallation = (typeof ROLES_INSTALLATION)[number];
export type RolesInstallations = Partial<Record<RoleInstallation, readonly number[]>>;
export type UtilisateurDroits = {
  roles: readonly string[];
  installations?: RolesInstallations;
};

/** Seul ROLE_ADMIN est global ; installations.admin reste limité à ses identifiants. */
export function estAdministrateurGlobal(utilisateur: UtilisateurDroits | null): boolean {
  return utilisateur?.roles?.includes('ROLE_ADMIN') ?? false;
}

/** Ignore les claims absents ou mal formés, sans convertir de chaînes en identifiants. */
function installationsDuRole(
  utilisateur: UtilisateurDroits | null,
  role: RoleInstallation,
): number[] {
  const ids = utilisateur?.installations?.[role];
  return Array.isArray(ids)
    ? ids.filter((id): id is number => typeof id === 'number' && Number.isSafeInteger(id) && id > 0)
    : [];
}

/** Renvoie tous les rôles locaux : aucune priorité n’efface les autres attributions. */
export function rolesSurInstallation(
  utilisateur: UtilisateurDroits | null,
  installationId: number,
): RoleInstallation[] {
  return ROLES_INSTALLATION.filter((role) =>
    installationsDuRole(utilisateur, role).includes(installationId),
  );
}

/** L’absence d’identifiant sert aux entrées globales ; un identifiant invalide est toujours refusé. */
export function aRoleInstallation(
  utilisateur: UtilisateurDroits | null,
  roles: readonly RoleInstallation[],
  installationId?: number,
): boolean {
  if (
    installationId !== undefined &&
    (!Number.isSafeInteger(installationId) || installationId <= 0)
  )
    return false;
  if (estAdministrateurGlobal(utilisateur)) return true;
  return installationId === undefined
    ? roles.some((role) => installationsDuRole(utilisateur, role).length > 0)
    : rolesSurInstallation(utilisateur, installationId).some((role) => roles.includes(role));
}

/** La consultation nécessite au moins un rôle sur l’installation demandée. */
export function peutConsulterInstallation(
  utilisateur: UtilisateurDroits | null,
  installationId?: number,
): boolean {
  return aRoleInstallation(utilisateur, ROLES_INSTALLATION, installationId);
}

/** Les MOA, mainteneurs et administrateurs locaux peuvent gérer les planifications de leur périmètre. */
export function peutPlanifierInstallation(
  utilisateur: UtilisateurDroits | null,
  installationId?: number,
): boolean {
  return aRoleInstallation(utilisateur, ['mo', 'maint', 'admin'], installationId);
}
